import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Log to security system (production only)
    if (!import.meta.env.DEV) {
      this.logErrorToSecurity(error, errorInfo);
    }
  }

  private async logErrorToSecurity(error: Error, errorInfo: ErrorInfo) {
    try {
      // Parse stack trace for readability
      const stackLines = error.stack?.split('\n').slice(0, 10) || [];
      const formattedStack = stackLines.join('\n');

      // Extract file location from stack
      const stackMatch = error.stack?.match(/at .+ \((.+):(\d+):(\d+)\)/);
      const filePath = stackMatch?.[1] || 'unknown';
      const lineNumber = stackMatch?.[2] ? parseInt(stackMatch[2]) : undefined;
      
      // Determine risk level
      const isCritical = error.message?.toLowerCase().includes('security') || 
                         error.message?.toLowerCase().includes('crash');
      const riskLevel = isCritical ? 'critical' : 'high';

      const errorDetails = {
        error_type: 'application_error',
        error_message: error.message,
        error_name: error.name,
        stack_trace: formattedStack,
        component_stack: errorInfo.componentStack,
        route: window.location.pathname,
        file_path: filePath,
        line_number: lineNumber,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        caught_by: 'ErrorBoundary'
      };

      await supabase.rpc('log_security_event', {
        p_event_type: 'application_error',
        p_resource_type: 'error',
        p_resource_id: null,
        p_organization_id: null,
        p_details: JSON.stringify(errorDetails),
        p_risk_level: riskLevel
      });
    } catch (loggingError) {
      // Don't throw if logging fails
      console.error('[ErrorBoundary] Failed to log error to security system:', loggingError);
    }
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <CardTitle>Something went wrong</CardTitle>
              <CardDescription>
                We encountered an unexpected error. Please try refreshing the page or contact support@zoocertica.com for assistance.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button 
                onClick={() => window.location.reload()} 
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Page
              </Button>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 text-left">
                  <summary className="cursor-pointer text-sm text-muted-foreground">
                    Error Details (Development)
                  </summary>
                  <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;